import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import LinearLoading from "../../shared/components/LinearLoading";
import DownloadIcon from "mdi-react/DownloadIcon";
import PhoneIcon from "mdi-react/PhoneIcon";
import EmailIcon from "mdi-react/EmailOutlineIcon";
import MailingIcon from "mdi-react/MapMarkerOutlineIcon";
import ChevronLeftIcon from "mdi-react/ChevronLeftIcon";
import AlertCircleIcon from "mdi-react/AlertCircleOutlineIcon";
import RefreshIcon from "mdi-react/RefreshIcon";
import moment from "moment";
import { _formatNumber, _formatPrice } from "../../shared/helpers/utils";
import { _axiosCall } from "../../shared/helpers/apicalls";
import { getMockRun } from "./mockData";

const apiUrl = process.env.API_ROOT;

const TYPE_LABELS = {
	expired: "Expired",
	pom: "Permanently off-market",
	tom: "Temporarily off-market"
};

const STATUS_BANNERS = {
	complete: null,
	partial: {
		tone: "warn",
		title: "Some contacts couldn't be matched",
		body: "We were unable to find owner contact info for a few properties. Those rows are marked below."
	},
	loading: {
		tone: "info",
		title: "Skip-trace still in progress",
		body: "Owner contacts are still being looked up. This page will refresh automatically; the email will be sent once all owners are processed."
	},
	skipped: {
		tone: "info",
		title: "Skip-trace was off for this run",
		body: "Listings only. Enable skip-trace from Edit Alert to include owner phone, email, and mailing address in future digests."
	},
	failed: {
		tone: "error",
		title: "Skip-trace failed",
		body: "Owner contact lookup couldn't be completed. The listings below were still found and emailed."
	},
	no_credit: {
		tone: "warn",
		title: "Premium Credit balance exhausted",
		body: "Your balance ran out partway through this run. Some properties were traced before the cut-off; the rest are marked “Skipped — out of Premium Credits” below. Top up or enable auto-recharge to keep future runs uninterrupted."
	},
	// Used when auto-recharge was on at run-time but couldn't top the balance up.
	// Branched in _renderBanner so the message points to "check your card", not
	// "enable auto-recharge".
	no_credit_with_autorecharge: {
		tone: "warn",
		title: "Auto-recharge couldn't top up your balance",
		body: "Auto-recharge was enabled for this run but didn't complete a purchase, so the balance ran out mid-trace. Some properties were traced before the cut-off; the rest are marked “Skipped — out of Premium Credits” below. Check your card on file or top up manually to unblock future runs."
	}
};

class AlertRunDetail extends PureComponent {
	state = {
		loading: true,
		error: null,
		run: null,
		subscription: null
	};

	componentDidMount() {
		this._load();
	}

	componentDidUpdate(prev) {
		if (
			prev.location.search !== this.props.location.search ||
			(prev.match && prev.match.params.runId) !== (this.props.match && this.props.match.params.runId)
		) {
			this._load();
		}
	}

	componentWillUnmount() {
		if (this._refreshTimer) clearTimeout(this._refreshTimer);
	}

	_load = () => {
		const params = new URLSearchParams(this.props.location.search);
		const mockState = params.get("state");

		// Dev override: ?state=complete|partial|... uses the local mock data so
		// we can review edge-state UI without seeding real runs.
		if (mockState) {
			const data = getMockRun(mockState);
			this.setState({ loading: false, error: null, run: data.run, subscription: data.subscription });
			return;
		}

		const { user } = this.props;
		const runId = this.props.match && this.props.match.params && this.props.match.params.runId;
		if (!user || !user.id) {
			this.setState({ loading: false, error: "Please log in to view this run." });
			return;
		}
		if (!runId) {
			this.setState({ loading: false, error: "Missing run id." });
			return;
		}

		this.setState({ loading: true, error: null });
		_axiosCall({
			url: `${apiUrl}/expired-listing-alerts/${encodeURIComponent(user.id)}/runs/${encodeURIComponent(runId)}`,
			method: "get"
		})
			.then(res => {
				if (!res || res.error) {
					this.setState({ loading: false, error: (res && res.error) || "Could not load run." });
					return;
				}
				this.setState({
					loading: false,
					run: res.run,
					subscription: res.subscription
				});
				// While skip-trace is still mid-flight, refresh in 10s.
				if (res.run && (res.run.status === "querying" || res.run.status === "tracing" || res.run.status === "enriching")) {
					this._refreshTimer = setTimeout(this._load, 10000);
				}
			})
			.catch(() => this.setState({ loading: false, error: "Could not load run." }));
	};

	_renderHeader() {
		const { run, subscription } = this.state;
		const date = moment(run.runAt).format("MMM D, YYYY");
		const window = moment(run.sinceDate).format("MMM D") + " – " + moment(run.untilDate).format("MMM D, YYYY");
		const statusLabel = run.skipTraceEnabled ? "Skip-trace " + run.skipTraceStatus : "Listings only";

		return (
			<div style={{ marginBottom: 16 }}>
				<div style={{ marginBottom: 8 }}>
					<a
						href="/expired-listings/alerts"
						style={{ color: "#780F9E", fontSize: 13, textDecoration: "none", fontWeight: 500 }}
						onClick={e => { e.preventDefault(); this.props.history.push("/expired-listings/alerts"); }}
					>
						<ChevronLeftIcon size={16} style={{ marginTop: -2 }} /> Back to My Alerts
					</a>
				</div>
				<h3 className="page-title" style={{ marginBottom: 4 }}>
					{subscription.name}
				</h3>
				<div style={{ color: "#666", fontSize: 14 }}>
					Run on {date} · Covers {window} · <span style={{ textTransform: "capitalize" }}>{statusLabel}</span>
				</div>
			</div>
		);
	}

	_renderStats() {
		const { run } = this.state;
		const recharges = run.autoRechargeCount || 0;
		// `sentCount` (and `properties` list) is what we emailed. We used to
		// show "X of Y" where Y = matchCount (Solr numFound for the window),
		// but that conflated "in window" with "available to email" and
		// confused users. Show the actionable number up front; the bigger
		// pool numbers go in a hover for transparency.
		const emailed = (run.properties && run.properties.length) || run.sentCount || 0;
		const attempted = run.attemptedCount || 0;
		const totalInWindow = run.matchCount || 0;
		const tooltipBits = [];
		if (attempted > 0) tooltipBits.push(`${attempted} skip-traced`);
		if (totalInWindow > 0) tooltipBits.push(`${totalInWindow} matched in window`);
		const listingLabel = _formatNumber(emailed);
		const stats = [
			{
				label: "Listings emailed",
				value: listingLabel,
				tooltip: tooltipBits.length > 0 ? tooltipBits.join(" · ") : null
			},
			run.skipTraceEnabled && { label: "Contacts found", value: _formatNumber(this._contactCount()) },
			run.skipTraceEnabled && { label: "Premium Credits used", value: _formatNumber(run.creditsUsed || 0) },
			run.skipTraceEnabled && recharges > 0 && {
				label: "Auto-recharges",
				value: _formatNumber(recharges)
			}
		].filter(Boolean);

		return (
			<div className="alert-run__stats">
				{stats.map((s, i) => {
					const inner = (
						<div className="alert-run__stat" key={i}>
							<div className="alert-run__stat__label">{s.label}</div>
							<div className="alert-run__stat__value">{s.value}</div>
						</div>
					);
					return s.tooltip ? (
						<Tooltip key={i} title={s.tooltip} arrow>{inner}</Tooltip>
					) : inner;
				})}
			</div>
		);
	}

	// One contact per matched owner — multi-owner deeds count as 2 (or 3) so
	// the displayed total lines up with credits charged. Falls back to legacy
	// "any phones/emails on the aggregate" count for runs persisted before
	// the per-owner refactor.
	_contactCount = () => {
		const { run } = this.state;
		if (!run || !run.properties) return 0;
		return run.properties.reduce((sum, p) => {
			const owners = p.contact && Array.isArray(p.contact.owners) ? p.contact.owners : null;
			if (owners) return sum + owners.length;
			const legacyHit = p.contact && (
				(p.contact.phones && p.contact.phones.length > 0) ||
				(p.contact.emails && p.contact.emails.length > 0)
			);
			return sum + (legacyHit ? 1 : 0);
		}, 0);
	};

	_renderBanner() {
		const { run } = this.state;
		// Swap in the auto-recharge-aware copy when the user had auto-recharge on
		// at run time but the balance still ran out — telling them to "enable
		// auto-recharge" when it's already enabled is misleading.
		const key = (run.skipTraceStatus === "no_credit" && run.autoRechargeEnabledAtRun)
			? "no_credit_with_autorecharge"
			: run.skipTraceStatus;
		const banner = STATUS_BANNERS[key];
		if (!banner) return null;
		const styles = {
			info: { background: "#EEF4FF", border: "1px solid #C7D7FE", color: "#2A4365" },
			warn: { background: "#FFF7E6", border: "1px solid #FFD8A8", color: "#7C4A0F" },
			error: { background: "#FFF5F5", border: "1px solid #FED7D7", color: "#9B2C2C" }
		}[banner.tone];
		return (
			<div style={Object.assign({ borderRadius: 8, padding: "12px 14px", marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }, styles)}>
				{banner.tone === "info" && <RefreshIcon size={20} style={{ flexShrink: 0, marginTop: 1 }} />}
				{banner.tone !== "info" && <AlertCircleIcon size={20} style={{ flexShrink: 0, marginTop: 1 }} />}
				<div>
					<div style={{ fontWeight: 600, marginBottom: 2 }}>{banner.title}</div>
					<div style={{ fontSize: 13, lineHeight: 1.5 }}>{banner.body}</div>
					{run.skipTraceError && (
						<div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{run.skipTraceError}</div>
					)}
				</div>
			</div>
		);
	}

	_renderDownloadCTA() {
		const { run } = this.state;
		const ready = run.status === "complete" && (run.properties || []).length > 0;
		// Public S3 URL with an unguessable GUID in the key — uploaded at
		// run-time, no expiration. A plain link click downloads straight from
		// S3, no backend round-trip. Old runs (before S3 upload was wired)
		// won't have csvUrl set; show disabled state.
		const directUrl = run.csvUrl || null;
		const tip = !ready
			? (run.status === "complete" ? "No listings to download for this run." : "CSV will be available once skip-trace finishes.")
			: directUrl
				? "Download as CSV with all contact fields"
				: "CSV not generated for this run.";
		return (
			<div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
				<Tooltip title={tip} arrow>
					<span>
						<Button
							variant="contained"
							size="large"
							disabled={!ready || !directUrl}
							href={directUrl || undefined}
							style={{
								background: ready && directUrl ? "#780F9E" : "#DBE0E8",
								color: "#fff",
								textTransform: "none",
								fontWeight: 600,
								padding: "10px 18px"
							}}
						>
							<DownloadIcon size={20} style={{ marginRight: 8 }} />
							Download full list (CSV)
						</Button>
					</span>
				</Tooltip>
				{run.testMode && (
					<span style={{ background: "#fff7e6", border: "1px solid #ffd8a8", borderRadius: 6, padding: "4px 10px", fontSize: 12, color: "#7c4a0f", fontWeight: 600 }}>
						TEST RUN — dummy contacts
					</span>
				)}
			</div>
		);
	}

	_renderRow = (p, idx) => {
		const offDate = p.off_market_date ? moment(p.off_market_date).format("MMM D, YYYY") : "—";
		const offLabel = TYPE_LABELS[p.off_market_type] || "Off-market";
		const c = p.contact;
		const owners = c && Array.isArray(c.owners) ? c.owners : null;
		const hasPhone = c && c.phones && c.phones.length > 0;
		const hasEmail = c && c.emails && c.emails.length > 0;
		const status = p.skip_trace_status;

		return (
			<tr key={p.property_id} style={{ borderTop: "1px solid #eee", verticalAlign: "top" }}>
				<td style={{ padding: "14px 12px", minWidth: 240 }}>
					<a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: "#780F9E", textDecoration: "none", fontWeight: 600, fontSize: 15 }}>
						{p.address}
					</a>
					<div style={{ color: "#555", fontSize: 13, marginTop: 2 }}>
						{[p.unit, p.neighborhood, p.borough].filter(Boolean).join(" · ")}
					</div>
					<div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
						{offLabel} · {offDate}
					</div>
				</td>
				<td style={{ padding: "14px 12px", textAlign: "right", whiteSpace: "nowrap", minWidth: 130 }}>
					<div style={{ fontWeight: 700, color: "#111", fontSize: 15 }}>{_formatPrice(p.price)}</div>
					<div style={{ color: "#666", fontSize: 12, marginTop: 2 }}>
						{p.bedrooms} bd · {p.baths} ba · {_formatNumber(p.sq_ft)} sf
					</div>
				</td>
				<td style={{ padding: "14px 12px", minWidth: 280 }}>
					{status === "off" && <span style={{ color: "#999", fontSize: 13 }}>Skip-trace was off</span>}
					{status !== "off" && (
						<Fragment>
							{status === "pending" && (
								<span style={{ color: "#2A4365", fontSize: 13 }}>
									<RefreshIcon size={14} style={{ marginTop: -2, marginRight: 4 }} />
									Looking up owner…
								</span>
							)}
							{status === "complete" && owners && owners.length > 0 ? (
								// Per-owner blocks (multi-owner deeds get one block each).
								owners.map((o, i) => (
									<div
										key={i}
										style={{
											marginTop: i > 0 ? 8 : 0,
											paddingTop: i > 0 ? 8 : 0,
											borderTop: i > 0 ? "1px dotted #e8e2f0" : "none"
										}}
									>
										<div style={{ fontWeight: 600, marginBottom: 4 }}>{o.owner_name || "Owner"}</div>
										{o.phones && o.phones.length > 0 && (
											<div style={{ fontSize: 13, color: "#333", marginBottom: 2 }}>
												<PhoneIcon size={14} style={{ marginTop: -2, marginRight: 4, color: "#780F9E" }} />
												{o.phones.join(" · ")}
											</div>
										)}
										{o.emails && o.emails.length > 0 && (
											<div style={{ fontSize: 13, color: "#333", marginBottom: 2 }}>
												<EmailIcon size={14} style={{ marginTop: -2, marginRight: 4, color: "#780F9E" }} />
												{o.emails.join(" · ")}
											</div>
										)}
										{o.mailing_address && (
											<div style={{ fontSize: 12, color: "#666" }}>
												<MailingIcon size={14} style={{ marginTop: -2, marginRight: 4 }} />
												{o.mailing_address}
											</div>
										)}
									</div>
								))
							) : status === "complete" && c ? (
								// Legacy aggregate for runs persisted before per-owner detail.
								<Fragment>
									{(c.owner_name || p.owner) && (
										<div style={{ fontWeight: 600, marginBottom: 4 }}>{c.owner_name || p.owner}</div>
									)}
									{hasPhone && (
										<div style={{ fontSize: 13, color: "#333", marginBottom: 2 }}>
											<PhoneIcon size={14} style={{ marginTop: -2, marginRight: 4, color: "#780F9E" }} />
											{c.phones.join(" · ")}
										</div>
									)}
									{hasEmail && (
										<div style={{ fontSize: 13, color: "#333", marginBottom: 2 }}>
											<EmailIcon size={14} style={{ marginTop: -2, marginRight: 4, color: "#780F9E" }} />
											{c.emails.join(" · ")}
										</div>
									)}
									{c.mailing_address && (
										<div style={{ fontSize: 12, color: "#666" }}>
											<MailingIcon size={14} style={{ marginTop: -2, marginRight: 4 }} />
											{c.mailing_address}
										</div>
									)}
									{!hasPhone && !hasEmail && (
										<div style={{ fontSize: 13, color: "#888" }}>Owner identified, no reachable contact</div>
									)}
								</Fragment>
							) : null}
							{(status === "no_match" || status === "no_owner" || status === "failed" || status === "no_credit") && (
								<Fragment>
									{p.owner && (
										<div style={{ fontWeight: 600, marginBottom: 4 }}>{p.owner}</div>
									)}
									{(status === "no_match" || status === "no_owner") && (
										<div style={{ color: "#888", fontSize: 13 }}>No contact found</div>
									)}
									{status === "failed" && (
										<div style={{ color: "#9B2C2C", fontSize: 13 }}>Lookup failed</div>
									)}
									{status === "no_credit" && (
										<div style={{ color: "#a36100", fontSize: 13 }}>Skipped — out of Premium Credits</div>
									)}
								</Fragment>
							)}
						</Fragment>
					)}
				</td>
			</tr>
		);
	};

	_renderStateSwitcher() {
		// Dev-only — toggles the mock state via URL param. Remove once the
		// backend run-fetch is wired up.
		if (process.env.NODE_ENV === "production") return null;
		const params = new URLSearchParams(this.props.location.search);
		const current = params.get("state") || "complete";
		const states = ["complete", "partial", "loading", "skipped", "failed", "empty"];
		return (
			<div className="alert-run__state-switcher">
				<strong>Mock state</strong>
				{states.map(s => (
					<a
						key={s}
						href={"?state=" + s}
						className={s === current ? "active" : ""}
						onClick={e => {
							e.preventDefault();
							this.props.history.replace(this.props.location.pathname + "?state=" + s);
						}}
					>
						{s}
					</a>
				))}
			</div>
		);
	}

	render() {
		const { loading, error, run, subscription } = this.state;

		if (loading) {
			return (
				<Container className="dashboard"><Row><Col md={12}><LinearLoading /></Col></Row></Container>
			);
		}

		if (error) {
			return (
				<Container className="dashboard">
					<Row><Col md={12}>
						<Card><CardBody>
							<div style={{ padding: 32, textAlign: "center" }}>
								<AlertCircleIcon size={28} style={{ color: "#d32f2f", marginBottom: 8 }} />
								<div style={{ color: "#666" }}>{error}</div>
								<div style={{ marginTop: 14 }}>
									<Button onClick={() => this.props.history.push("/expired-listings/alerts")} style={{ textTransform: "none", color: "#780F9E" }}>
										← Back to My Alerts
									</Button>
								</div>
							</div>
						</CardBody></Card>
					</Col></Row>
				</Container>
			);
		}

		if (!run || !subscription) {
			return (
				<Container className="dashboard">
					<Row><Col md={12}>
						<Card><CardBody>
							<div style={{ padding: 32, textAlign: "center", color: "#666" }}>Run not found.</div>
						</CardBody></Card>
					</Col></Row>
				</Container>
			);
		}

		const isEmpty = !run.properties || run.properties.length === 0;
		const skipTraceCol = run.skipTraceEnabled;

		return (
			<Container className="dashboard">
				<Row>
					<Col md={12}>{this._renderHeader()}</Col>
				</Row>
				<Row>
					<Col md={12}>
						<Card><CardBody>
							{this._renderStats()}
							{this._renderBanner()}
							{!isEmpty && this._renderDownloadCTA()}

							{isEmpty && (
								<div style={{ padding: 40, textAlign: "center", color: "#666" }}>
									<div style={{ fontSize: 16, fontWeight: 600, color: "#444", marginBottom: 6 }}>No new listings this run</div>
									<div style={{ fontSize: 13 }}>Nothing matched your filters in the run window. We'll keep watching for tomorrow.</div>
								</div>
							)}

							{!isEmpty && (
								<table style={{ width: "100%", borderCollapse: "collapse" }}>
									<thead>
										<tr style={{ borderBottom: "2px solid #eee", textAlign: "left" }}>
											<th style={{ padding: "10px 12px", fontSize: 12, color: "#888", textTransform: "uppercase" }}>Property</th>
											<th style={{ padding: "10px 12px", fontSize: 12, color: "#888", textTransform: "uppercase", textAlign: "right" }}>Price · BR/BA/SF</th>
											{skipTraceCol && (
												<th style={{ padding: "10px 12px", fontSize: 12, color: "#888", textTransform: "uppercase" }}>Owner contact</th>
											)}
										</tr>
									</thead>
									<tbody>{run.properties.map(this._renderRow)}</tbody>
								</table>
							)}
						</CardBody></Card>
					</Col>
				</Row>
				{this._renderStateSwitcher()}
			</Container>
		);
	}
}

export default withRouter(connect(state => ({ user: state.user }))(AlertRunDetail));
